/* eslint-disable react/react-in-jsx-scope */
import { LoadingSpinner } from "@/components/ui/loadingSpinner";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { convertEuroToCurrency } from "@/libs/amount";
import {
  addMonths,
  getChartDataMonth,
  getChartDataTrimestre,
  getChartDataYear,
} from "@/libs/utils";
import { usePreviewPerformance } from "@/store/store";
import { Indicator, ProjectType } from "@/types";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

const Chart: FC<{
  project: ProjectType;
  indicator: Indicator;
  currentYear: number;
  currencySelected?: string;
  isCumulative?: boolean;
}> = ({
  project,
  indicator,
  currentYear,
  currencySelected = "EUR",
  isCumulative,
}) => {
    const { t } = useTranslation("dashboard", {
      keyPrefix: "program",
    });
    const chartConfig = {
      prevu: {
        label: t("Prévu"),
        color: "hsl(var(--color-blue))",
      },
      realise: {
        label: t("Réalisé"),
        color: "hsl(var(--color-green))",
      },
      verifie: {
        label: t("Verifié / justifié / certifié"),
        color: "hsl(var(--color-orange))",
      },
    } satisfies ChartConfig;

    const { previews } = usePreviewPerformance();
    const indicatorForChart = useMemo(
      () => ({
        ...indicator,
        cumulativeReporting:
          isCumulative ?? Boolean((indicator as any)?.cumulativeReporting),
      }),
      [indicator, isCumulative]
    );
    const reportingIndicatorValuesForChart = useMemo(
      () =>
        (project.reportingIndicatorValues ?? []).map((item) => ({
          ...item,
          indicator: {
            ...(item.indicator as Record<string, unknown>),
            cumulativeReporting: isCumulative ?? Boolean((indicator as any)?.cumulativeReporting),
          },
        })),
      [
        project.reportingIndicatorValues,
        isCumulative,
        indicatorForChart.cumulativeReporting,
      ]
    );

    const launchDate = new Date(project.launchDate).getFullYear();
    const expectedDuration = Number(project.expectedDuration) || 42;
    const dateWithAddedMonths = addMonths(project.launchDate, expectedDuration);
    const expectedDurationYear = !dateWithAddedMonths
      ? 0
      : dateWithAddedMonths?.getFullYear() == launchDate
        ? 1
        : dateWithAddedMonths.getFullYear() - (launchDate - 1);

    const [data, setData] = useState<any[] | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const devises = JSON.parse(localStorage.getItem("devises") || "[]");

    useEffect(() => {
      let isActive = true;

      (async () => {
        if (isActive) {
          setData(null);
        }
        setIsLoading(true);

        let dataTemp =
          previews[indicator.name] == "2"
            ? getChartDataTrimestre(
              reportingIndicatorValuesForChart,
              expectedDurationYear,
              indicatorForChart,
              launchDate,
              "project"
            )
            : previews[indicator.name] == "1"
              ? getChartDataMonth(
                reportingIndicatorValuesForChart,
                currentYear,
                indicatorForChart,
                "project"
              )
              : getChartDataYear(
                reportingIndicatorValuesForChart,
                indicatorForChart,
                expectedDurationYear,
                launchDate,
                project.parameters,
                undefined,
                "project"
              );

        if (indicator.code === "euro_recus") {
          dataTemp = await Promise.all(
            dataTemp.map(async (item) => {
              if (currencySelected === "EUR") {
                return item;
              }

              const prevuConverted = await convertEuroToCurrency(
                item.prevu,
                devises.find((dev) => dev.code === currencySelected)
              );
              const realiseConverted = await convertEuroToCurrency(
                item.realise,
                devises.find((dev) => dev.code === currencySelected)
              );
              const verifieConverted = await convertEuroToCurrency(
                item.verifie,
                devises.find((dev) => dev.code === currencySelected)
              );

              return {
                ...item,
                prevu: prevuConverted,
                realise: realiseConverted,
                verifie: verifieConverted,
              };
            })
          );
        }

        if (indicator.code === "euro_recus") {
          // console.log(dataTemp)
        }

        const sanitizedData = Array.isArray(dataTemp)
          ? dataTemp.map((item) => ({
            ...item,
            prevu: Number.isFinite(Number(item?.prevu)) ? Number(item.prevu) : 0,
            realise: Number.isFinite(Number(item?.realise))
              ? Number(item.realise)
              : 0,
            verifie: Number.isFinite(Number(item?.verifie))
              ? Number(item.verifie)
              : 0,
          }))
          : [];

        if (isActive) {
          setData(sanitizedData);
          setIsLoading(false);
        }
      })();

      return () => {
        isActive = false;
      };
    }, [
      previews,
      currencySelected,
      currentYear,
      expectedDurationYear,
      launchDate,
      project.parameters,
      reportingIndicatorValuesForChart,
      indicatorForChart,
    ]);

    return (
      <div className="relative min-h-[200px] w-full">
        {(isLoading || data == null) && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[20px] bg-card/70 backdrop-blur-[1px]">
            <LoadingSpinner />
          </div>
        )}
        {data != null && !isLoading && (
          <ChartContainer config={chartConfig} className="min-h-[200px] w-full">
            <BarChart accessibilityLayer data={data} barGap={2}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="legend"
                tickLine={false}
                tickMargin={10}
                axisLine={false}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickCount={4}
                tick={({ x, y, payload }) => (
                  <text
                    x={x}
                    y={y + 10}
                    textAnchor="middle"
                    fill="#000"
                    fontSize={12}
                  >
                    {Number(payload.value)
                      .toLocaleString("fr-FR")
                      .replace(/\u202F/g, " ")}
                  </text>
                )}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="prevu" fill={chartConfig.prevu.color} radius={20} />
              <Bar dataKey="realise" fill={chartConfig.realise.color} radius={20} />
              <Bar dataKey="verifie" fill={chartConfig.verifie.color} radius={20} />
            </BarChart>
          </ChartContainer>
        )}
      </div>
    );
  };

export default Chart;
